// Angular imports
import { Inject, Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';

// 3rd party imports
import { forkJoin, Observable, Subject } from 'rxjs';
import { map } from 'rxjs/operators';
import { serialize, deserialize, plainToClass, classToPlain } from 'class-transformer';
import { alert } from 'devextreme/ui/dialog';

// CC-Common imports
import { CCLocalStorageCategory, CCLocalStorageService, Utils } from 'cc-common';

// Component imports
import { MissingWidgetComponent } from '../missing-widget/missing-widget.component';

// Service imports
import { AuthService } from '../../_services/auth.service';
import { UserService } from '../../_services/data-services/user.service';
import { ContextManagerService } from '../../_services/data-services/context-manager.service';
import { MPCacheRegistrationService } from '../../_services/shared-services/mp-cache-registration-service';

// Constant imports
import { URLConstants } from '../../_constants/url-constants';
import { CacheDuration } from '../../_constants/cache-duration';

// Model imports
import { SystemDashboardsFactory } from '../_interfaces/system-dashboards-factory';
import { IDynamicDashboardWidgetsFactory } from '../_interfaces/dynamic-dashboard-widgets-factory';
import { ILocalContextFactory } from '../_interfaces/local-context-factory';
import { DynamicDashboardEntry } from '../_model/dynamic-dashboard-entry';
import { DynamicDashboardWidget } from '../_model/dynamic-dashboard-widget';
import { ActionAccessListParameterDto } from '../../_models/action-access-list-parameter-dto';
import { SharedWorkspaceResultDto } from '../_model/shared-workspace-result-dto';
import { SharedWorkspaceParametersDto } from '../_model/shared-workspace-parameters-dto';
import { AddEditSharedWorkspaceParametersDto } from '../_model/add-edit-shared-workspace-parameters-dto';
import { DeleteSharedWorkspaceParametersDto } from '../_model/delete-shared-workspace-parameters-dto';

class DynamicDashboardRecord {
    defaultId: string;
    dashboards: Array<string>;
}

@Injectable()
export class DynamicDashboardService {
    refreshed = new Subject<boolean>();
    selectionChanged = new Subject<DynamicDashboardEntry>();
    isFullScreenWidget = new Subject<boolean>();

    defaultId: string;
    dashboardWidgets: DynamicDashboardWidget[];
    userDashboards: DynamicDashboardEntry[] = [];
    systemDashboards: DynamicDashboardEntry[] = [];
    sharedDashboards: DynamicDashboardEntry[] = [];

    dashboardChangedKey: string;
    dashboardClientId: string;
    dashboardTimer: any;

    constructor(
        @Inject('StorageKey') private readonly storageKey: string,
        @Inject('WidgetsFactory') private readonly widgetsFactory: IDynamicDashboardWidgetsFactory,
        @Inject('DashboardsFactory') private readonly dashboardsFactory: SystemDashboardsFactory,
        @Inject('LocalContextFactory') private readonly localContextFactory: ILocalContextFactory,
        private readonly httpClient: HttpClient,
        private readonly authService: AuthService,
        private readonly userService: UserService,
        private readonly contextService: ContextManagerService,
        private readonly localStorageService: CCLocalStorageService,
        private readonly cacheRegistrationService: MPCacheRegistrationService
    ) {
        this.authService.getSubjectLoginSuccess().subscribe((flag) => {
            if (Utils.isTrue(flag)) {
                this.dashboardWidgets = null;
            }
        });
        this.dashboardChangedKey = `${storageKey}-changed`.toUpperCase();
        this.dashboardClientId = Utils.getUUID();
        localStorage.removeItem(this.dashboardChangedKey);
        this.dashboardTimer = setInterval(this.hasNoConfigConflict.bind(this), 10000);
    }

    hasNoConfigConflict(): boolean {
        const clientId = localStorage.getItem(this.dashboardChangedKey);
        if (Utils.notNullAndDefined(clientId) && Utils.notEquals(clientId, this.dashboardClientId)) {
            clearInterval(this.dashboardTimer);
            alert(
                `Workspace configuration has changed and needs to be reloaded before making new changes!.<br />Click 'OK' to reload the page`,
                'Configuration Conflict'
            ).then(() => {
                window.location.reload();
            });
            return false;
        }
        return true;
    }

    updateLocalContext(context: any): void {
        if (Utils.notNullAndDefined(this.localContextFactory)) {
            this.localContextFactory.updateContext(context);
        }
    }

    getLocalContext(): Observable<any> {
        return Utils.notNullAndDefined(this.localContextFactory)
            ? this.localContextFactory.getContext()
            : new Observable();
    }

    getLocalContextInfo(): any {
        return Utils.notNullAndDefined(this.localContextFactory) ? this.localContextFactory.getLocalContext() : null;
    }

    getMissingWidget(): DynamicDashboardWidget {
        return new DynamicDashboardWidget(
            'MISSING_WIDGET',
            'MISSING_WIDGET',
            'Missing Widget',
            MissingWidgetComponent,
            false,
            false
        );
    }

    getDashboardWidgets(): Observable<DynamicDashboardWidget[]> {
        return new Observable((observer) => {
            if (Utils.notNullOrEmpty(this.dashboardWidgets)) {
                observer.next(this.dashboardWidgets);
            } else {
                this.loadDashboardWidgets().subscribe(() => {
                    observer.next(this.dashboardWidgets);
                });
            }
        });
    }

    getGroupedDashboardWidgets(dashboardWidgets: DynamicDashboardWidget[]): any {
        const groupedDashboardWidgets = [];
        const categories = Utils.uniqueList(
            dashboardWidgets.filter((w) => Utils.notNullOrEmpty(w.category)).map((w) => w.category)
        );
        if (categories.length > 0) {
            categories.forEach((category) => {
                const items = dashboardWidgets.filter((w) => Utils.equalsIgnoreCase(w.category, category));
                const subCategories = Utils.uniqueList(
                    items.filter((w) => Utils.notNullOrEmpty(w.subCategory)).map((w) => w.subCategory)
                );
                if (subCategories.length > 0) {
                    const subItems = [];
                    subCategories.forEach((subCategory) => {
                        subItems.push({
                            name: subCategory,
                            expanded: true,
                            items: items.filter((w) => Utils.equalsIgnoreCase(w.subCategory, subCategory))
                        });
                    });
                    groupedDashboardWidgets.push({
                        name: category,
                        expanded: true,
                        items: subItems
                    });
                } else {
                    groupedDashboardWidgets.push({
                        name: category,
                        expanded: true,
                        items
                    });
                }
            });
        }
        return groupedDashboardWidgets;
    }

    getNewDashboardId(): number {
        let startId = 0;
        if (this.systemDashboards.length > 0) {
            startId = Math.max(startId, this.systemDashboards[this.systemDashboards.length - 1].id);
        }
        if (this.userDashboards.length > 0) {
            startId = Math.max(startId, this.userDashboards[this.userDashboards.length - 1].id);
        }
        return startId + 1;
    }

    getSystemDashboards(): DynamicDashboardEntry[] {
        return this.systemDashboards;
    }

    getUserDashboards(): DynamicDashboardEntry[] {
        return this.userDashboards;
    }

    getDashboards(clearCache = false): Observable<DynamicDashboardEntry[]> {
        return this.loadDashboards(clearCache).pipe(
            map(() => {
                let dashboards = this.systemDashboards.concat(this.userDashboards);
                const defaultDashboard = this.getDefaultDashboard();
                if (Utils.notNullAndDefined(defaultDashboard) && Utils.isFalse(defaultDashboard.isSystem)) {
                    dashboards = [defaultDashboard].concat(
                        dashboards.filter((d) => Utils.notEquals(d.id, defaultDashboard.id))
                    );
                }
                return dashboards;
            })
        );
    }

    saveDashboard(dashboard: DynamicDashboardEntry): Observable<boolean> {
        return new Observable((observer) => {
            if (this.hasNoConfigConflict()) {
                const saveLocalDashboard = (clearCache = false) => {
                    this.userDashboards = this.userDashboards.filter((d) => Utils.notEquals(d.id, dashboard.id));
                    if (dashboard.isDefault) {
                        this.userDashboards.forEach((d) => (d.isDefault = false));
                    }
                    if (!dashboard.isSystem) {
                        this.userDashboards.push(dashboard);
                    }
                    this.defaultId = null;
                    this.getDefaultDashboard();
                    this.saveUserDashboardRecord(this.userDashboards);
                    this.loadDashboards(clearCache).subscribe(() => observer.next(true));
                    localStorage.setItem(this.dashboardChangedKey, this.dashboardClientId);
                };

                if (Utils.isTrue(dashboard.isShared) || Utils.isTrue(dashboard.isSystem)) {
                    dashboard.isShared = dashboard.isSystem || dashboard.isShared;
                    const params = new AddEditSharedWorkspaceParametersDto();
                    params.name = dashboard.name;
                    params.categoryCode = this.getWorkspaceCategoryCode();
                    params.isSystemType = dashboard.isSystem;
                    params.config = dashboard.toConfigString();
                    params.id = Utils.notNullAndDefined(dashboard.sharedWorkspace)
                        ? dashboard.sharedWorkspace.id
                        : null;
                    params.allowExternalUsers = dashboard.allowExternalUsers;
                    const serviceMethod = Utils.notNullAndDefined(params.id)
                        ? this.updateSharedWorkspaces(params)
                        : this.saveSharedWorkspaces(params);
                    serviceMethod.subscribe(
                        (result) => {
                            dashboard.sharedWorkspace = result;
                            saveLocalDashboard();
                        },
                        (error) => {
                            observer.error(error);
                        }
                    );
                } else if (
                    Utils.notNullAndDefined(dashboard.sharedWorkspace) &&
                    (Utils.equalsIgnoreCase(
                        dashboard.sharedWorkspace.createdBy.id,
                        this.userService.getCurrentUserId()
                    ) ||
                        this.userService.isITUser())
                ) {
                    const postDeleteWorkspace = () => {
                        dashboard.isShared = false;
                        dashboard.isSystem = false;
                        dashboard.sharedWorkspace = null;
                        saveLocalDashboard();
                    };
                    const params = new DeleteSharedWorkspaceParametersDto();
                    params.id = dashboard.sharedWorkspace.id;
                    params.key = dashboard.sharedWorkspace.key;
                    this.deleteSharedWorkspaces(params).subscribe(
                        () => postDeleteWorkspace(),
                        () => postDeleteWorkspace()
                    );
                } else {
                    saveLocalDashboard();
                }
            }
        });
    }

    deleteDashboard(dashboard: DynamicDashboardEntry, deleteSharedWorkspace = false): Observable<boolean> {
        return new Observable((observer) => {
            if (this.hasNoConfigConflict()) {
                const deleteLocalDashboard = (clearCache = false) => {
                    this.userDashboards = this.userDashboards.filter((d) => Utils.notEquals(d.id, dashboard.id));
                    this.defaultId = null;
                    this.getDefaultDashboard();
                    this.saveUserDashboardRecord(this.userDashboards);
                    this.loadDashboards(clearCache).subscribe(() => observer.next(true));
                    localStorage.setItem(this.dashboardChangedKey, this.dashboardClientId);
                };
                if (Utils.isTrue(deleteSharedWorkspace) && Utils.notNullAndDefined(dashboard.sharedWorkspace)) {
                    const params = new DeleteSharedWorkspaceParametersDto();
                    params.id = dashboard.sharedWorkspace.id;
                    params.key = dashboard.sharedWorkspace.key;
                    this.deleteSharedWorkspaces(params).subscribe(
                        () => {
                            deleteLocalDashboard();
                        },
                        () => {
                            deleteLocalDashboard();
                        }
                    );
                } else {
                    deleteLocalDashboard();
                }
            }
        });
    }

    markDefaultDashboard(dashboard: DynamicDashboardEntry): Observable<boolean> {
        return new Observable((observer) => {
            if (this.hasNoConfigConflict()) {
                this.defaultId = dashboard.id.toString();
                this.systemDashboards
                    .filter((d) => Utils.notEquals(d.id, dashboard.id))
                    .forEach((d) => (d.isDefault = false));
                this.userDashboards
                    .filter((d) => Utils.notEquals(d.id, dashboard.id))
                    .forEach((d) => (d.isDefault = false));

                this.saveUserDashboardRecord(this.userDashboards);
                this.loadDashboards().subscribe(() => observer.next(true));
                localStorage.setItem(this.dashboardChangedKey, this.dashboardClientId);
            }
        });
    }

    getDefaultDashboard(): DynamicDashboardEntry {
        let defaultDashboard: DynamicDashboardEntry = null;
        const dashboards = this.systemDashboards.concat(this.userDashboards);
        if (dashboards.length > 0) {
            if (!!this.defaultId) {
                defaultDashboard = dashboards.find((d) => Utils.equals(d.id, parseInt(this.defaultId)));
            }
            if (!defaultDashboard) {
                defaultDashboard = dashboards.find((d) => Utils.isTrue(d.isDefault)) || dashboards[0];
            }
            this.defaultId = defaultDashboard.id.toString();
            dashboards.forEach((d) => (d.isDefault = Utils.equals(d.id, defaultDashboard.id)));
        }
        return defaultDashboard;
    }

    getWorkspaceCategoryCode(): 'USR' | 'CB' | 'COMM' | 'PROP' | 'CCB' {
        return this.dashboardsFactory.getDashboardCategoryCode();
    }

    getSharedWorkspaces(params: SharedWorkspaceParametersDto): Observable<SharedWorkspaceResultDto[]> {
        const url = `${URLConstants.ccUserBaseApiUrl}/sharedworkspace?${params.getQueryString()}`;
        this.cacheRegistrationService.registerForCache(url, params.clearCache);
        const headers = this.cacheRegistrationService.getHttpRequestHeaders(url, CacheDuration.fifteenMinute);
        const options = { headers };
        return this.httpClient
            .get(url, options)
            .pipe(map((response: any[]) => plainToClass(SharedWorkspaceResultDto, response)));
    }

    deleteUserDashboards(): void {
        const record: DynamicDashboardRecord = new DynamicDashboardRecord();
        record.defaultId = '';
        record.dashboards = [];
        this.localStorageService.setItem(this.storageKey, serialize(record), CCLocalStorageCategory.DASH);
        localStorage.setItem(this.dashboardChangedKey, this.dashboardClientId);
    }

    saveUserDashboards(userDashboards: DynamicDashboardEntry[]): void {
        const record: DynamicDashboardRecord = new DynamicDashboardRecord();
        record.defaultId = userDashboards[0].id.toString();
        record.dashboards = userDashboards.map((d) => d.toStorageString());
        this.localStorageService.setItem(this.storageKey, serialize(record), CCLocalStorageCategory.DASH);
        localStorage.setItem(this.dashboardChangedKey, this.dashboardClientId);
    }

    private saveUserDashboardRecord(userDashboards: DynamicDashboardEntry[]): void {
        userDashboards.sort(this.sortById);
        const record: DynamicDashboardRecord = new DynamicDashboardRecord();
        record.defaultId = this.defaultId;
        record.dashboards = userDashboards.map((d) => d.toStorageString());
        this.localStorageService.setItem(this.storageKey, serialize(record), CCLocalStorageCategory.DASH);
    }

    private saveSharedWorkspaces(params: AddEditSharedWorkspaceParametersDto): Observable<SharedWorkspaceResultDto> {
        const url = `${URLConstants.ccUserBaseApiUrl}/sharedworkspace`;
        return this.httpClient
            .post(url, classToPlain(params))
            .pipe(map((response: any) => plainToClass(SharedWorkspaceResultDto, response)));
    }

    private updateSharedWorkspaces(params: AddEditSharedWorkspaceParametersDto): Observable<SharedWorkspaceResultDto> {
        const url = `${URLConstants.ccUserBaseApiUrl}/sharedworkspace`;
        return this.httpClient
            .put(url, classToPlain(params))
            .pipe(map((response: any) => plainToClass(SharedWorkspaceResultDto, response)));
    }

    private deleteSharedWorkspaces(params: DeleteSharedWorkspaceParametersDto): Observable<any> {
        const url = `${URLConstants.ccUserBaseApiUrl}/sharedworkspace?${params.getQueryString()}`;
        const options = {
            headers: new HttpHeaders({ 'Content-Type': 'application/json' }),
            body: { id: params.id, key: params.key }
        };
        return this.httpClient.delete(url, options);
    }

    private loadDashboardWidgets(): Observable<boolean> {
        return new Observable((observer) => {
            const context = this.getLocalContextInfo() || this.contextService.getSelectedContext();
            const param: ActionAccessListParameterDto = new ActionAccessListParameterDto();
            if (!!context) {
                param.communityId = context.communityId;
                param.customerId = context.customerId;
            }
            this.userService.getActionList(param).subscribe((actions) => {
                const widgets = this.widgetsFactory.getDashboardWidgets();
                const accessCodes = actions.map((a) => a.code.toUpperCase());
                this.dashboardWidgets = widgets.filter((widget) => {
                    return Utils.isInList(widget.code.toUpperCase(), accessCodes);
                });
                observer.next(true);
            });
        });
    }

    private loadDashboards(clearCache = false): Observable<void> {
        return new Observable((observer) => {
            forkJoin([
                this.dashboardsFactory.getSystemDashboards(clearCache),
                this.dashboardsFactory.getSharedDashboards(clearCache)
            ]).subscribe((results) => {
                this.systemDashboards = results[0];
                this.sharedDashboards = results[1];

                this.systemDashboards.sort(this.sortById);
                this.sharedDashboards.sort(this.sortById);

                let startId = 0;
                if (this.systemDashboards.length > 0) {
                    startId = Math.max(startId, this.systemDashboards[this.systemDashboards.length - 1].id);
                }
                const userDashboards = [];
                const plainText = this.localStorageService.getItem(this.storageKey, CCLocalStorageCategory.DASH);
                if (!!plainText && !!plainText.length) {
                    const result: DynamicDashboardRecord = deserialize(DynamicDashboardRecord, plainText);
                    this.defaultId = result.defaultId;
                    result.dashboards.forEach((r) => {
                        const dashboard = deserialize(DynamicDashboardEntry, r);
                        const foundInShared =
                            Utils.notNullAndDefined(dashboard.sharedWorkspace) &&
                            this.sharedDashboards.length > 0 &&
                            Utils.notNullAndDefined(
                                this.sharedDashboards.find((s) =>
                                    Utils.equalsIgnoreCase(s.sharedWorkspace.key, dashboard.sharedWorkspace.key)
                                )
                            );
                        if (Utils.isFalse(foundInShared)) {
                            dashboard.isSystem = false;
                            dashboard.isShared = false;
                            dashboard.sharedWorkspace = null;
                        }
                        startId += 1;
                        dashboard.id = startId;
                        if (Utils.isTrue(dashboard.isDefault)) {
                            this.defaultId = dashboard.id.toString();
                        }
                        userDashboards.push(dashboard);
                    });
                }
                userDashboards.sort(this.sortById);

                this.userDashboards = userDashboards;
                this.refreshed.next(clearCache);
                observer.next();
            });
        });
    }

    private sortById(x: DynamicDashboardEntry, y: DynamicDashboardEntry): number {
        if (x.id > y.id) {
            return 1;
        } else if (x.id < y.id) {
            return -1;
        }
        return 0;
    }
}
